/**
 * RegistrationSheet — context-aware sign-up modal.
 *
 * Headline + sub-copy change per `context`. Form fields and auth logic are
 * identical across contexts.
 *
 * Contexts:
 *   save_scan        — guest tapped 'Save' on a scan
 *   trainer_enquiry  — guest tapped 'Send Enquiry' or 'Book Session'
 *   workout_start    — guest hit a hard-gated workout flow
 *   rate_limit       — guest hit 429 rate limit
 *   general          — neutral CTA from the bottom Sign Up button
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { useGuestStore, setGuestMode, trackGuest } from "../../store/guestStore";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

const CONTEXT_COPY = {
  save_scan: {
    headline: "Save your scan results",
    sub: "Your scan results are not saved yet. Create a free account to keep them.",
  },
  trainer_enquiry: {
    headline: "Connect with this trainer",
    sub: "Create a free account to send an enquiry and save trainer profiles.",
  },
  workout_start: {
    headline: "Start your fitness journey",
    sub: "Create a free account to access workouts, track progress, and earn FitPoints.",
  },
  rate_limit: {
    headline: "You've logged 5 meals",
    sub: "Create a free account to keep them and keep scanning.",
  },
  general: {
    headline: "Create your free account",
    sub: "Join 50,000 members training smarter with FitFrik.",
  },
  pro_apply: {
    headline: "Almost there — create your professional account",
    sub: "We need a quick account so we can verify your credentials and reply to you.",
  },
};

export default function RegistrationSheet({ context = "general", onSuccess, onDismiss }) {
  const register = useAuthStore((s) => s.register);
  const login = useAuthStore((s) => s.login);
  const pendingScans = useGuestStore((s) => s.pendingFoodScans);
  const clearGuestStore = useGuestStore((s) => s.clear);

  const [mode, setMode] = useState("signup"); // signup | login
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const copy = CONTEXT_COPY[context] || CONTEXT_COPY.general;

  const handleAuthSuccess = async () => {
    // Iter 50: use the batch /guest/migrate endpoint (idempotent, retry-
    // bounded server-side). Falls back to legacy per-item /nutrition/log
    // save for entries missing the new shape.
    const anon_id = useGuestStore.getState().anon_id;
    const gs = useGuestStore.getState();
    let saveAttempted = false;
    let saveSucceeded = true;
    if (pendingScans.length > 0) {
      saveAttempted = true;
      const shaped = pendingScans
        .filter((s) => s.client_scan_id && s.dish_name)
        .map((s) => ({
          client_scan_id: s.client_scan_id,
          dish_name: s.dish_name,
          calories: s.calories || 0,
          protein_g: s.protein_g || 0,
          carbs_g: s.carbs_g || 0,
          fat_g: s.fat_g || 0,
          portion_multiplier: s.portion_multiplier || 1,
          scanned_at: s.scanned_at || new Date().toISOString(),
          thumbnail_base64: s.thumbnail_base64 || null,
        }));
      if (shaped.length > 0 || (gs.savedProducts || []).length > 0) {
        try {
          await apiClient.post("/guest/migrate", {
            anon_id, scans: shaped,
            saved_products: (gs.savedProducts || []).map((r) => ({
              canonical: r.canonical, saved_at: r.saved_at,
            })),
          });
        } catch {
          saveSucceeded = false;
        }
      }
      // Legacy fallback for pre-iter50 entries
      const legacy = pendingScans.filter((s) => !s.client_scan_id);
      for (const scan of legacy) {
        try {
          await apiClient.post("/nutrition/log", {
            name: scan.name || scan.dish_name,
            calories: scan.calories, protein_g: scan.protein_g,
            carbs_g: scan.carbs_g, fat_g: scan.fats_g || scan.fat_g,
            portion_multiplier: scan.portion_multiplier || 1,
            source: "guest_retro_save",
          });
        } catch { saveSucceeded = false; }
      }
    }
    try {
      trackGuest("signup_completed", {
        context,
        retroactive_save_attempted: saveAttempted,
        retroactive_save_succeeded: saveSucceeded,
      });
    } catch { /* non-fatal */ }

    setGuestMode(false);
    clearGuestStore();
    onSuccess?.();
  };

  const submit = async () => {
    if (!email.trim() || !password.trim()) {
      return toast.error("Email and password are required");
    }
    setBusy(true);
    try {
      trackGuest("signup_shown", {
        context,
        scan_count_at_conversion: pendingScans.length,
      });
      if (mode === "signup") {
        if (!name.trim()) {
          setBusy(false);
          return toast.error("Name is required");
        }
        if (!ageConfirmed) {
          setBusy(false);
          return toast.error("Please confirm you are 13 years or older");
        }
        if (!termsAccepted) {
          setBusy(false);
          return toast.error("Please accept the Terms and Privacy Policy");
        }
        await register(name.trim(), email.trim(), password, {
          age_confirmed: ageConfirmed,
          terms_accepted: termsAccepted,
          marketing_consent: marketingConsent,
        });
      } else {
        await login(email.trim(), password);
      }
      await handleAuthSuccess();
    } catch (e) {
      toast.error(e.message || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onDismiss}
      className="fixed inset-0 z-[80] grid place-items-end"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="registration-sheet"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl mx-auto bg-white rounded-t-[24px] p-5"
        data-testid={`registration-sheet-${context}`}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 18px)", maxHeight: "92vh", overflowY: "auto" }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2
              className="font-barlow-cond"
              style={{ fontSize: 28, fontWeight: 800, color: "#1A1A1A", lineHeight: 1.05 }}
              data-testid="reg-headline"
            >
              {copy.headline}
            </h2>
            <p
              style={{
                fontFamily: "Manrope",
                fontSize: 13,
                fontWeight: 400,
                color: "#6B6760",
                marginTop: 6,
                maxWidth: 340,
              }}
              data-testid="reg-subcopy"
            >
              {copy.sub}
            </p>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Close"
            data-testid="reg-close"
            className="w-9 h-9 grid place-items-center rounded-full bg-canvas2"
          >
            <X size={16} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 p-1 rounded-pill bg-canvas2 mt-4">
          {[{ id: "signup", label: "Sign Up" }, { id: "login", label: "Log In" }].map((t) => {
            const on = mode === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setMode(t.id)}
                data-testid={`reg-mode-${t.id}`}
                className="flex-1 h-9 rounded-pill text-[12px] font-medium"
                style={{
                  background: on ? "#FFFFFF" : "transparent",
                  color: on ? "#1A1A1A" : "#6B6760",
                  border: on ? "1px solid #E2E0DC" : "1px solid transparent",
                  fontWeight: on ? 700 : 600,
                  borderRadius: 100,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {mode === "signup" && (
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="reg-name"
            className="w-full mt-3 h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          />
        )}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="reg-email"
          className="w-full mt-2 h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="reg-password"
          className="w-full mt-2 h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        />
        {mode === "signup" && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#4A4845", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                data-testid="reg-age-confirmed"
                style={{ marginTop: 2 }}
              />
              <span>I am 13 years or older.</span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#4A4845", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="reg-terms-accepted"
                style={{ marginTop: 2 }}
              />
              <span>
                I agree to FitFrik's{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#00D4FF", textDecoration: "underline" }}>Terms</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#00D4FF", textDecoration: "underline" }}>Privacy Policy</a>.
              </span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#4A4845", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                data-testid="reg-marketing-consent"
                style={{ marginTop: 2 }}
              />
              <span>Send me fitness tips and offers. <span style={{ color: "#8E8A84" }}>(optional)</span></span>
            </label>
          </div>
        )}
        <button
          onClick={submit}
          disabled={busy}
          data-testid="reg-submit"
          className="w-full mt-4 h-12 rounded-pill disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
        >
          {busy ? (mode === "signup" ? "Creating account…" : "Signing in…") : (mode === "signup" ? "Create account" : "Log in")}
        </button>

        {/* Iter 51: Google fast-signup — uses the existing OAuth redirect
            flow. On success, /auth/google/callback rehydrates the session
            and the guest-migrate call fires from the AuthCallback path
            once the user lands back on any authed page. */}
        <div className="mt-3 flex items-center gap-2" style={{ color: "#8E8A84" }}>
          <div className="flex-1 h-px" style={{ background: "#E2E0DC" }} />
          <span className="text-[11px] font-medium">or</span>
          <div className="flex-1 h-px" style={{ background: "#E2E0DC" }} />
        </div>
        <button
          type="button"
          onClick={() => {
            try {
              trackGuest("signup_google_clicked", { context });
            } catch { /* noop */ }
            const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            window.location.href = `${backend}/api/auth/google/login`;
          }}
          data-testid="reg-google"
          className="w-full mt-3 h-12 rounded-pill flex items-center justify-center gap-2"
          style={{
            background: "#FFFFFF",
            color: "#1A1A1A",
            border: "1px solid #E2E0DC",
            borderRadius: 100,
            fontWeight: 600,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.4 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 34.4 26.9 35.6 24 35.6c-5.2 0-9.7-3.3-11.3-8L6.1 33C9.3 39.4 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.6c-.5.4 7.1-5.2 7.1-15.1 0-1.3-.1-2.4-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <p style={{ fontFamily: "Manrope", fontSize: 11, color: "#8E8A84", textAlign: "center", marginTop: 12 }}>
          By continuing you agree to FitFrik's Terms & Privacy Policy.
        </p>
      </motion.div>
    </motion.div>
  );
}
